import { Stack, TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { Domains } from "../../data/Domains";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import VillageConsoleContext from "../../store/VillageConsoleContext";
import { VillageRulesDto } from "../../dtos/VillageRulesDto";
import { getCurrentVillageRule } from "./uploadRules.utils";


type UploadRulesParametersProps = {

    isUploading : boolean;
    selectCannotUploadRules : Dispatch< SetStateAction< boolean > >;
    selectIsThirdParty : Dispatch< SetStateAction< boolean > >;
    selectVersion : Dispatch< SetStateAction< string > >;
    villageRules : VillageRulesDto[ ];

};


export const UploadRulesParameters : React.FC< UploadRulesParametersProps > = ( props : UploadRulesParametersProps ) => {

    const { isUploading, selectCannotUploadRules, selectIsThirdParty, selectVersion, villageRules } = props;

    const { domain } = useContext( VillageConsoleContext );

    const [ canUploadBhpRules, setCanUploadBhpRules ] = useState< boolean >( true );
    const [ canUploadTpvRules, setCanUploadTpvRules ] = useState< boolean >( true );
    const [ helperText, setHelperText ] = useState< string >( );
    const [ isThirdParty, setIsThirdParty ] = useState< boolean >( false );
    const [ startFrom, setStartFrom ] = useState< string >( '' );
    const [ version, setVersion ] = useState< string >( '' );
    const [ versionError, setVersionError ] = useState< boolean >( false );
    const [ villageRule, setVillageRule ] = useState< VillageRulesDto >( );


    useEffect(( ) => {
        
        initialize( );

    }, [ villageRules ]);

    
    const calculateDates = ( ) => {

        let today = ( new Date() ).toISOString( ).slice( 0, 10 );
        setStartFrom( today );

    };


    const checkDates = () => {

        const today = ( new Date( ) ).toISOString( ).slice( 0, 10 );
        
        if( domain === Domains.Bma ) {

            const currentBhpRulesStartDate =  villageRules.find( vr => vr.endDateTime.toString()[0] === '0' && !vr.isThirdParty )?.startDateTime.toString( ).slice( 0, 10 );
            const currentTpvRulesStartDate =  villageRules.find( vr => vr.endDateTime.toString()[0] === '0' && vr.isThirdParty )?.startDateTime.toString( ).slice( 0, 10 );

            if( currentBhpRulesStartDate === today && currentTpvRulesStartDate === today )
            {

                selectCannotUploadRules( true );

            } else if( currentBhpRulesStartDate === today ) {

                // can upload only TPV rules
                setIsThirdParty( true );
                setCanUploadBhpRules( false );
                changeIsThirdParty( true );


            } else if( currentTpvRulesStartDate === today ) {

                // can upload only Bhp rules
                setIsThirdParty( false );
                setCanUploadTpvRules( false );

            } 

        } else {

            const currentRulesStartDate =  villageRules.find( vr => vr.endDateTime.toString()[0] === '0' )?.startDateTime.toString( ).slice( 0, 10 );

            if( currentRulesStartDate === today ){

                selectCannotUploadRules( true );

            }

        }


    };


    const checkVersion = ( currentVillageRuleVersion : string, version : string, isThirdParty? : boolean ) => {

        let versionAlreadyExists: boolean = false;
              
        if ( domain === Domains.Bma && villageRules.find( vr => vr.version.toLowerCase( ) === version.toLowerCase( ) && vr.isThirdParty === isThirdParty  ) ) {

            versionAlreadyExists = true;

        } else if ( domain !== Domains.Bma && villageRules.find( vr => vr.version.toLowerCase( ) === version.toLowerCase( ) ) ) {

            versionAlreadyExists = true;

        }

        if ( versionAlreadyExists ) {

            setVersionError( true );
            setHelperText( `Version ${version} already exists` );
            selectVersion( '' );
           

        } else {
            
            setVersionError( false );
            setHelperText( `Current Village Rules version is ${currentVillageRuleVersion}` );
            selectVersion( version );
            
        }

    };


    const clearState = ( ) => {

        setCanUploadBhpRules( true );
        setCanUploadTpvRules( true );
        setHelperText( undefined );
        setIsThirdParty( false );
        setStartFrom( '' );
        setVersion( '' );
        setVersionError( false );
        setVillageRule( undefined );

    };


    const changeIsThirdParty = ( isThirdParty : boolean ) => {

        setIsThirdParty( isThirdParty );
        selectIsThirdParty( isThirdParty ); 

        const villageRule = getCurrentVillageRule( domain, villageRules, isThirdParty );
        
        setVillageRule( villageRule );

        if( villageRule ){

            checkVersion( villageRule.version, version, isThirdParty );

        }

    };


    const handleIsThirdPartyChange = async ( event: SelectChangeEvent ) => {

        const isThirdParty =  Boolean( event.target.value );
        changeIsThirdParty( isThirdParty );

    };


    const handleVersionChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        const version = event.target.value;
        setVersion( version );
        checkVersion( villageRule?.version as string, version, isThirdParty );

    };


    const initialize = ( ) => {

        clearState( );
        calculateDates( );

        const villageRule = getCurrentVillageRule( domain, villageRules, isThirdParty );
        setVillageRule( villageRule );

        if( villageRule ) {
            
            setIsThirdParty( villageRule.isThirdParty );
            setHelperText( `Current Village Rules version is ${villageRule.version}` );
        }

        checkDates( );

    };

    return(

        <Stack direction="row" display="flex" justifyContent="center" gap={ 1 } >
                            
            <TextField 
                disabled={ isUploading }
                error={ versionError } 
                helperText={ helperText }
                label="Version" 
                onChange={ handleVersionChange }
                sx={{ width: '400px'}} 
                variant="outlined"
            />

            <TextField
                disabled
                // onChange={ ( event: React.ChangeEvent<HTMLInputElement> ) => setStartFrom( event.target.value ) }
                onKeyDown={ e  => e.preventDefault( ) }
                label="Effective From"
                type="date"
                value={ startFrom }
                sx={{ width: '400px' }}
                InputLabelProps={{ shrink: true }}
            />

            { 
                domain === Domains.Bma 
                    &&
                <FormControl sx={{ width: '400px' }}>
                    <InputLabel>Village</InputLabel>
                    <Select
                        disabled={ isUploading }
                        onChange={ handleIsThirdPartyChange }
                        value={ isThirdParty ? "1" : "0" }
                        label="Village">
                            { canUploadBhpRules && <MenuItem value={ 0 }>BHP Village</MenuItem> }
                            { canUploadTpvRules && <MenuItem value={ 1 }>Third Party Village</MenuItem> }
                    </Select>
                </FormControl>
            }

        </Stack>

    );

}; 